import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pill, Plus, Paperclip, Clock, ArrowsClockwise, WarningCircle } from "@phosphor-icons/react";
import { isParent, isAdmin, can } from "@/lib/roles";
import { SourceDocumentFields, useSourceDocuments } from "@/pages/admin/student/SourceDocuments";

const STATUS_TONE = { draft: "bg-amber-50 text-amber-800", active: "bg-emerald-50 text-emerald-700",
                      paused: "bg-slate-100 text-slate-600", ended: "bg-slate-200 text-slate-600" };
const blank = { medication: "", dose: "", route: "Oral", instructions: "", storage: "", schedule_times: [],
                window_minutes: 30, start_date: "", end_date: "", authorized_staff: [],
                licensing_acknowledged: false, licensing_reference: "", reason: "",
                authorization: { authorized_by: "", relationship: "", authorization_date: "", expires_on: "",
                                 prescriber: "", document_name: "", document_path: "", version: 1 } };

export const MedicationsPanel = ({ student, user }) => {
  const [plans, setPlans] = useState([]);
  const [meta, setMeta] = useState({ routes: [], outcomes: {}, statuses: [] });
  const [history, setHistory] = useState({ administrations: [], escalations: [] });
  const [form, setForm] = useState(null);
  const [times, setTimes] = useState("");
  const [saving, setSaving] = useState(false);
  const [renewals, setRenewals] = useState([]);
  const [renewal, setRenewal] = useState(null);
  const [renewalTimes, setRenewalTimes] = useState("");
  const [docTypes, setDocTypes] = useState({});
  const canEdit = can(user, "health", "edit") && !isParent(user);
  const canActivate = canEdit && isAdmin(user);
  const { docs, reloadDocs } = useSourceDocuments(student.id, "medication");

  const load = useCallback(async () => {
    try {
      const [p, m, h] = await Promise.all([
        api.get("/medications/plans", { params: { student_id: student.id } }),
        api.get("/medications/meta"),
        api.get("/medications/history", { params: { student_id: student.id, limit: 50 } }),
      ]);
      setPlans(p.data); setMeta(m.data); setHistory(h.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    try {
      const { data } = await api.get("/medications/renewals", { params: { status: "pending" } });
      setRenewals(data.filter((r) => r.student_id === student.id));
    } catch { setRenewals([]); }
  }, [student.id]);
  useEffect(() => { load(); }, [load]);

  const startRenewal = async (plan) => {
    try {
      const { data } = await api.get("/health/review-policy");
      setDocTypes(data.medication_document_types || {});
    } catch { /* a non-director cannot read the policy; the dialog still works */ }
    setRenewal({ plan_id: plan.id, medication: plan.medication, dose: plan.dose || "",
                 route: plan.route || "Oral", instructions: plan.instructions || "",
                 window_minutes: plan.window_minutes || 30, authorized_by: "", relationship: "",
                 prescriber: "", authorization_date: "", expires_on: "", document_id: "", note: "" });
    setRenewalTimes((plan.schedule_times || []).join(", "));
  };

  const saveRenewal = async () => {
    setSaving(true);
    const { plan_id: planId, medication, ...rest } = renewal;
    try {
      await api.post(`/medications/plans/${planId}/renewal`, {
        ...rest, schedule_times: renewalTimes.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Renewal filed as a pending version — the current plan is unchanged");
      setRenewal(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const activateRenewal = async (r) => {
    try {
      await api.post(`/medications/renewals/${r.id}/activate`, { reviewer_name: user?.name || "", note: "" });
      toast.success("Renewal activated — the previous authorization version is preserved");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.missing) toast.error(`Needs documentation — still missing ${d.missing.join("; ")}`);
      else toast.error(apiError(d));
    }
  };

  const open = (plan) => {
    const next = plan ? { ...blank, ...plan, authorization: { ...blank.authorization, ...(plan.authorization || {}) } } : { ...blank };
    setForm(next);
    setTimes((next.schedule_times || []).join(", "));
  };

  const save = async () => {
    setSaving(true);
    const payload = { ...form, student_id: student.id,
                      schedule_times: times.split(",").map((t) => t.trim()).filter(Boolean) };
    ["id", "student_name", "room", "status", "created_by", "created_by_name", "created_at", "updated_at",
     "updated_by", "status_reason", "status_changed_by", "status_changed_at"].forEach((k) => delete payload[k]);
    try {
      if (form.id) await api.put(`/medications/plans/${form.id}`, payload);
      else await api.post("/medications/plans", payload);
      toast.success("Medication plan saved");
      setForm(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const setStatus = async (plan, status) => {
    try {
      await api.post(`/medications/plans/${plan.id}/status`, { status, reason: "" });
      toast.success(`Plan ${status}`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.missing) toast.error(`Cannot activate yet — still needs ${d.missing.join("; ")}`);
      else toast.error(apiError(d));
    }
  };

  const upload = async (plan, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/medications/plans/${plan.id}/authorization`, fd);
      toast.success("Authorization filed");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-white rounded-2xl border border-border p-6" data-testid="medications-panel">
      <div className="flex justify-between items-start gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Pill size={18} /> Scheduled medication</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Only entered from written authorization and instructions. Nothing here is created from a free-text note,
            and a dose is never suggested by the system.
          </p>
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => open(null)} className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                  data-testid="add-medication-btn"><Plus size={15} className="mr-1" /> Add plan</Button>
        )}
      </div>

      <div className="mt-4 space-y-3">
        {plans.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-medications">No medication plan on file.</p>}
        {plans.map((p) => (
          <div key={p.id} className="rounded-2xl border border-border p-4" data-testid={`medication-${p.id}`}>
            <div className="flex flex-wrap gap-2 items-center">
              <p className="font-bold">{p.medication}</p>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_TONE[p.status] || "bg-muted"}`}
                    data-testid={`medication-status-${p.id}`}>{p.status}</span>
              <span className="text-xs font-semibold text-muted-foreground">
                Authorization v{(p.authorization || {}).version || 1}
                {(p.authorization || {}).expires_on ? ` · expires ${p.authorization.expires_on}` : ""}
              </span>
              {(p.authorization || {}).expires_on && p.authorization.expires_on < new Date().toISOString().slice(0, 10) && (
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-red-50 text-red-700"
                      data-testid={`medication-auth-expired-${p.id}`}>Authorization expired</span>
              )}
              {canEdit && (
                <span className="ml-auto flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="rounded-full font-bold" onClick={() => startRenewal(p)}
                          data-testid={`renew-medication-${p.id}`}><ArrowsClockwise size={13} className="mr-1" /> Renew</Button>
                  <Button size="sm" variant="outline" className="rounded-full font-bold" onClick={() => open(p)}
                          data-testid={`edit-medication-${p.id}`}>Edit</Button>
                  <label className="cursor-pointer inline-flex items-center gap-1 px-3 py-1 rounded-full border border-border font-bold text-sm">
                    <Paperclip size={13} /> Authorization
                    <input type="file" className="hidden" accept="image/*,.pdf"
                           onChange={(e) => upload(p, e.target.files?.[0])} data-testid={`medication-doc-${p.id}`} />
                  </label>
                  {p.status !== "active" && (
                    <Button size="sm" className="rounded-full bg-[#10B981] hover:bg-emerald-600 font-bold"
                            onClick={() => setStatus(p, "active")} data-testid={`activate-medication-${p.id}`}>Make active</Button>
                  )}
                  {p.status === "active" && (
                    <Button size="sm" variant="outline" className="rounded-full font-bold"
                            onClick={() => setStatus(p, "paused")} data-testid={`pause-medication-${p.id}`}>Pause</Button>
                  )}
                  {p.status !== "ended" && (
                    <Button size="sm" variant="ghost" className="rounded-full text-[#EF4444] font-bold"
                            onClick={() => setStatus(p, "ended")} data-testid={`end-medication-${p.id}`}>End</Button>
                  )}
                </span>
              )}
            </div>
            <dl className="mt-3 grid sm:grid-cols-2 gap-2 text-sm">
              {[["Authorized dose", p.dose], ["Route", p.route], ["Scheduled times", (p.schedule_times || []).join(", ")],
                ["Window", p.window_minutes ? `${p.window_minutes} min` : ""], ["Start", p.start_date], ["End", p.end_date],
                ["Storage", p.storage], ["Instructions", p.instructions],
                ["Authorized by", (p.authorization || {}).authorized_by], ["Prescriber", (p.authorization || {}).prescriber],
                ["Authorized staff", (p.authorized_staff || []).join(", ")],
                ["Document", (p.authorization || {}).document_name]]
                .filter(([, v]) => v).map(([k, v]) => (
                <div key={k}><dt className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{k}</dt>
                  <dd className="font-semibold whitespace-pre-wrap">{v}</dd></div>
              ))}
            </dl>
            {(p.authorization_history || []).length > 0 && (
              <p className="mt-2 text-xs font-semibold text-muted-foreground"
                 data-testid={`medication-prior-versions-${p.id}`}>
                {p.authorization_history.length} earlier authorization version
                {p.authorization_history.length === 1 ? "" : "s"} preserved on file.
              </p>
            )}
          </div>
        ))}
      </div>

      {renewals.length > 0 && (
        <div className="mt-5 space-y-3" data-testid="medication-renewals">
          <h3 className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Pending renewals</h3>
          {renewals.map((r) => (
            <div key={r.id} className="rounded-2xl border border-amber-300 bg-amber-50 p-4"
                 data-testid={`medication-renewal-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-bold">{r.medication}</p>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${(r.missing || []).length ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}
                      data-testid={`renewal-state-${r.id}`}>
                  {(r.missing || []).length ? "Needs documentation" : "Ready for review"}
                </span>
                {r.authorization?.expires_on && (
                  <span className="text-xs font-semibold text-muted-foreground">New expiry {r.authorization.expires_on}</span>
                )}
                {canActivate && (
                  <Button size="sm" onClick={() => activateRenewal(r)}
                          className="ml-auto rounded-full bg-[#10B981] hover:bg-emerald-600 font-bold"
                          data-testid={`activate-renewal-${r.id}`}>Review and activate</Button>
                )}
              </div>
              <p className="mt-2 text-xs font-semibold">
                The current plan keeps running until this renewal is activated.
              </p>
              {(r.missing || []).length > 0 && (
                <p className="mt-1 text-xs font-semibold text-red-700 flex items-start gap-1"
                   data-testid={`renewal-missing-${r.id}`}>
                  <WarningCircle size={14} className="mt-0.5 shrink-0" /> Still needs: {r.missing.join("; ")}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {history.administrations.length > 0 && (
        <div className="mt-5">
          <h3 className="text-xs uppercase tracking-wider font-bold text-muted-foreground flex items-center gap-1">
            <Clock size={13} /> Administration history
          </h3>
          <ul className="mt-2 space-y-1 text-sm" data-testid="medication-history">
            {history.administrations.map((a) => (
              <li key={a.id} className="font-semibold" data-testid={`medication-record-${a.id}`}>
                {(a.actual_time || "").slice(0, 16).replace("T", " ")} · {a.medication} · {meta.outcomes[a.outcome] || a.outcome}
                {a.dose_given ? ` · ${a.dose_given}` : ""} · {a.staff_name}
                {a.notes ? ` — ${a.notes}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      {form && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="medication-dialog">
            <DialogHeader><DialogTitle className="font-display">{form.id ? "Edit" : "Add"} medication plan</DialogTitle>
              <DialogDescription>
                Enter exactly what the signed authorization says. A plan only becomes active once the authorization,
                dose, route, instructions and licensing confirmation are all on file.
              </DialogDescription></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-sm font-bold">Medication</Label>
                <Input value={form.medication} onChange={(e) => setForm({ ...form, medication: e.target.value })}
                       className="mt-1 h-11 rounded-xl" data-testid="med-name" /></div>
              <div><Label className="text-sm font-bold">Authorized dose (as written)</Label>
                <Input value={form.dose} onChange={(e) => setForm({ ...form, dose: e.target.value })}
                       className="mt-1 h-11 rounded-xl" data-testid="med-dose" /></div>
              <div><Label className="text-sm font-bold">Route</Label>
                <Select value={form.route} onValueChange={(v) => setForm({ ...form, route: v })}>
                  <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="med-route"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.routes.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-sm font-bold">Scheduled times (HH:MM, comma separated)</Label>
                <Input value={times} onChange={(e) => setTimes(e.target.value)} placeholder="08:30, 12:30"
                       className="mt-1 h-11 rounded-xl" data-testid="med-times" /></div>
              <div><Label className="text-sm font-bold">Window (minutes)</Label>
                <Input type="number" min="5" max="240" value={form.window_minutes}
                       onChange={(e) => setForm({ ...form, window_minutes: Number(e.target.value) })}
                       className="mt-1 h-11 rounded-xl" data-testid="med-window" /></div>
              <div><Label className="text-sm font-bold">Authorized staff (emails, comma separated)</Label>
                <Input value={(form.authorized_staff || []).join(", ")}
                       onChange={(e) => setForm({ ...form, authorized_staff: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                       className="mt-1 h-11 rounded-xl" data-testid="med-staff" /></div>
              <div><Label className="text-sm font-bold">Start date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                       className="mt-1 h-11 rounded-xl" data-testid="med-start" /></div>
              <div><Label className="text-sm font-bold">End date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                       className="mt-1 h-11 rounded-xl" data-testid="med-end" /></div>
              <div className="sm:col-span-2"><Label className="text-sm font-bold">Administration instructions (as written)</Label>
                <Textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                          className="mt-1 rounded-xl" data-testid="med-instructions" /></div>
              <div className="sm:col-span-2"><Label className="text-sm font-bold">Storage instructions</Label>
                <Input value={form.storage} onChange={(e) => setForm({ ...form, storage: e.target.value })}
                       className="mt-1 h-11 rounded-xl" data-testid="med-storage" /></div>
              {[["authorized_by", "Authorized by (parent / guardian)"], ["relationship", "Relationship"],
                ["prescriber", "Prescriber"], ["authorization_date", "Authorization date", "date"],
                ["expires_on", "Authorization expires", "date"]].map(([k, label, type]) => (
                <div key={k}><Label className="text-sm font-bold">{label}</Label>
                  <Input type={type || "text"} value={form.authorization[k] || ""}
                         onChange={(e) => setForm({ ...form, authorization: { ...form.authorization, [k]: e.target.value } })}
                         className="mt-1 h-11 rounded-xl" data-testid={`med-auth-${k}`} /></div>
              ))}
              <div className="sm:col-span-2"><Label className="text-sm font-bold">Licensing / policy reference</Label>
                <Input value={form.licensing_reference} onChange={(e) => setForm({ ...form, licensing_reference: e.target.value })}
                       placeholder="e.g. Arkansas CCLU minimum licensing standard reviewed with the director"
                       className="mt-1 h-11 rounded-xl" data-testid="med-licensing-ref" /></div>
              <label className="sm:col-span-2 flex items-start gap-3 text-sm font-semibold">
                <input type="checkbox" checked={Boolean(form.licensing_acknowledged)} className="mt-1"
                       onChange={(e) => setForm({ ...form, licensing_acknowledged: e.target.checked })}
                       data-testid="med-licensing-ack" />
                I have checked the applicable childcare licensing rules and this center's medication policy before
                enabling administration.
              </label>
              <div className="sm:col-span-2"><Label className="text-sm font-bold">Reason for this entry / change</Label>
                <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                       className="mt-1 h-11 rounded-xl" data-testid="med-reason" /></div>
            </div>
            <Button onClick={save} disabled={saving || !form.medication}
                    className="w-full h-11 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="save-medication-btn">{saving ? "Saving…" : "Save plan"}</Button>
          </DialogContent>
        </Dialog>
      )}
      {renewal && (
        <Dialog open onOpenChange={() => setRenewal(null)}>
          <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="renewal-dialog">
            <DialogHeader><DialogTitle className="font-display">Renew authorization — {renewal.medication}</DialogTitle>
              <DialogDescription>
                This is filed as a pending version beside the current plan. The live plan keeps running and is only
                replaced once a director reviews a complete renewal. Every earlier version stays on file.
              </DialogDescription></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-sm font-bold">Authorized dose (as written)</Label>
                <Input value={renewal.dose} onChange={(e) => setRenewal({ ...renewal, dose: e.target.value })}
                       className="mt-1 h-11 rounded-xl" data-testid="renewal-dose" /></div>
              <div><Label className="text-sm font-bold">Route</Label>
                <Select value={renewal.route} onValueChange={(v) => setRenewal({ ...renewal, route: v })}>
                  <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="renewal-route"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.routes.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-sm font-bold">Scheduled times (HH:MM)</Label>
                <Input value={renewalTimes} onChange={(e) => setRenewalTimes(e.target.value)} placeholder="08:30, 12:30"
                       className="mt-1 h-11 rounded-xl" data-testid="renewal-times" /></div>
              <div><Label className="text-sm font-bold">Window (minutes)</Label>
                <Input type="number" min="5" max="240" value={renewal.window_minutes}
                       onChange={(e) => setRenewal({ ...renewal, window_minutes: Number(e.target.value) })}
                       className="mt-1 h-11 rounded-xl" data-testid="renewal-window" /></div>
              {[["authorized_by", "Authorized by (parent / guardian)"], ["relationship", "Relationship"],
                ["prescriber", "Prescriber"], ["authorization_date", "Authorization date", "date"],
                ["expires_on", "New authorization expires", "date"]].map(([k, label, type]) => (
                <div key={k}><Label className="text-sm font-bold">{label}</Label>
                  <Input type={type || "text"} value={renewal[k] || ""}
                         onChange={(e) => setRenewal({ ...renewal, [k]: e.target.value })}
                         className="mt-1 h-11 rounded-xl" data-testid={`renewal-${k}`} /></div>
              ))}
              <div className="sm:col-span-2"><Label className="text-sm font-bold">Administration instructions (as written)</Label>
                <Textarea value={renewal.instructions} onChange={(e) => setRenewal({ ...renewal, instructions: e.target.value })}
                          className="mt-1 rounded-xl" data-testid="renewal-instructions" /></div>
              <div className="sm:col-span-2">
                <SourceDocumentFields studentId={student.id} context="medication" types={docTypes} docs={docs}
                                      value={renewal.document_id} testPrefix="renewal"
                                      onChange={(id) => setRenewal({ ...renewal, document_id: id })}
                                      onFiled={reloadDocs} />
              </div>
              <div className="sm:col-span-2"><Label className="text-sm font-bold">Note to file</Label>
                <Input value={renewal.note} onChange={(e) => setRenewal({ ...renewal, note: e.target.value })}
                       className="mt-1 h-11 rounded-xl" data-testid="renewal-note" /></div>
            </div>
            <Button onClick={saveRenewal} disabled={saving}
                    className="w-full h-11 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="renewal-save">{saving ? "Filing…" : "File pending renewal"}</Button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
